/**
 * OpenStreetMap Overpass API クライアント。
 *
 * 避難場所一帯のポリゴンを「構成する複数施設のpolygonを取得 → 凸包で結合」
 * の方針で実装する。
 *
 * 商用利用時はOSMライセンス (ODbL) の帰属表示が必要。
 * "© OpenStreetMap contributors" を出力時に併記する。
 */
import {
  buffer as turfBuffer,
  convex,
  union,
  featureCollection,
  polygon as turfPolygon,
  lineString as turfLineString,
  length as turfLength,
  nearestPointOnLine,
  point as turfPoint,
} from '@turf/turf';
import type { Feature, FeatureCollection, Polygon, Position } from 'geojson';

/**
 * Overpass API のミラーエンドポイント。
 *
 * 1つ目から順に試行、HTTP 429/504/空応答/JSON parse fail などの transient error なら
 * 次のエンドポイントへフォールバック。Overpass は無料公開サーバなので頻繁に「too busy」になる。
 */
const OVERPASS_ENDPOINTS = [
  'https://overpass-api.de/api/interpreter',
  'https://overpass.kumi.systems/api/interpreter',
  'https://overpass.private.coffee/api/interpreter',
];

export class OverpassError extends Error {
  constructor(
    message: string,
    public readonly cause?: unknown,
  ) {
    super(message);
    this.name = 'OverpassError';
  }
}

interface OverpassElement {
  type: 'node' | 'way' | 'relation';
  id: number;
  tags?: Record<string, string>;
  geometry?: Array<{ lat: number; lon: number }>;
  members?: Array<{
    type: 'node' | 'way';
    role: string;
    geometry?: Array<{ lat: number; lon: number }>;
  }>;
}

interface OverpassResponse {
  elements: OverpassElement[];
}

export type AreaCombineMode =
  | 'union' // 各施設のpolygonを合体 (実際の形状に近い)
  | 'convex'; // 全点の凸包 (シンプル・近似)

/**
 * 指定座標 (center) 周辺 radius メートル以内で、指定された名前を持つ施設の
 * polygon境界を取得し、合体した1つのpolygonを返す。
 *
 * - 'union' (デフォルト): 各施設の閉じたpolygonを turf.union で合体。
 *   実際の建物・公園の輪郭を保ったままの形状になる。複数polygonに分かれていれば
 *   外接convex hullで囲む。
 * - 'convex': 全頂点の凸包。シンプルだが施設境界の凹みは表現できない。
 */
export async function fetchAreaPolygonByLandmarks(args: {
  center: { lng: number; lat: number };
  radiusMeters: number;
  landmarkNames: string[];
  combineMode?: AreaCombineMode;
}): Promise<Array<[number, number]>> {
  const {
    center,
    radiusMeters,
    landmarkNames,
    combineMode = 'union',
  } = args;

  // 名称マッチはスペース許容にする。OSMは「本願寺 津村別院」のように
  // 語間スペース入りで登録されることがあり、公式名「本願寺津村別院」と
  // 表記が揺れるため、各文字の間にスペース類を許すパターンで両方に一致させる。
  // NOTE: Overpass の正規表現は POSIX ERE で `\s` 非対応 → 半角スペースと
  //       全角スペース (U+3000) の文字クラスを使う。
  const nameRegex = landmarkNames
    .map((name) =>
      name
        .replace(/\s+/g, '') // 入力側のスペースは除去して正規化
        .split('')
        .map(escapeRegex)
        .join('[ \u3000]*'), // 半角スペース + 全角スペース (U+3000)
    )
    .join('|');
  const query = `
    [out:json][timeout:25];
    (
      way["name"~"^(${nameRegex})$"](around:${radiusMeters},${center.lat},${center.lng});
      relation["name"~"^(${nameRegex})$"](around:${radiusMeters},${center.lat},${center.lng});
    );
    out geom;
  `;

  const data = await fetchOverpass(query);

  // 各 element から「閉じたpolygon」を抽出
  const polygons: Array<Feature<Polygon>> = [];
  const allPoints: Position[] = [];

  for (const el of data.elements) {
    if (el.geometry && el.geometry.length >= 3) {
      const ring = el.geometry.map((p) => [p.lon, p.lat] as Position);
      for (const pt of ring) allPoints.push(pt);
      // 始点≠終点なら閉じる
      const first = ring[0]!;
      const last = ring[ring.length - 1]!;
      const fx = first[0];
      const fy = first[1];
      if (fx !== undefined && fy !== undefined && (fx !== last[0] || fy !== last[1])) {
        ring.push([fx, fy]);
      }
      try {
        polygons.push(turfPolygon([ring]));
      } catch {
        // 不正なring (自己交差等) はスキップ
      }
    }
    if (el.members) {
      for (const member of el.members) {
        if (member.geometry) {
          for (const pt of member.geometry) {
            allPoints.push([pt.lon, pt.lat]);
          }
        }
      }
    }
  }

  if (allPoints.length < 3) {
    throw new OverpassError(
      `指定したランドマーク (${landmarkNames.join(', ')}) のジオメトリが見つかりませんでした`,
    );
  }

  if (combineMode === 'union' && polygons.length > 0) {
    let result: Feature<Polygon> | null = polygons[0]!;
    for (let i = 1; i < polygons.length; i++) {
      try {
        const merged = union(featureCollection([result!, polygons[i]!]));
        if (merged && merged.geometry.type === 'Polygon') {
          result = merged as Feature<Polygon>;
        } else if (merged && merged.geometry.type === 'MultiPolygon') {
          // 分離したpolygonは凸包でまとめて1つに
          const hull = convex(featureCollection([result!, polygons[i]!]));
          if (hull && hull.geometry.type === 'Polygon') {
            result = hull as Feature<Polygon>;
          }
        }
      } catch {
        // unionに失敗 (turfの数値誤差等) したら凸包にフォールバック
        const hull = convex(featureCollection([result!, polygons[i]!]));
        if (hull && hull.geometry.type === 'Polygon') {
          result = hull as Feature<Polygon>;
        }
      }
    }
    if (result) {
      const ring = result.geometry.coordinates[0] ?? [];
      return ring.map(([lng, lat]) => [lng, lat] as [number, number]);
    }
  }

  // フォールバック: 全点の凸包
  const fc: FeatureCollection = {
    type: 'FeatureCollection',
    features: allPoints.map((p) => ({
      type: 'Feature',
      properties: {},
      geometry: { type: 'Point', coordinates: p },
    })),
  };
  const hull = convex(fc);
  if (!hull || hull.geometry.type !== 'Polygon') {
    throw new OverpassError('ポリゴンの結合に失敗しました');
  }
  const ring = (hull.geometry as Polygon).coordinates[0] ?? [];
  return ring.map(([lng, lat]) => [lng, lat] as [number, number]);
}

/** 閉じたリングの符号なし面積 (度^2, 相対比較用)。 */
function ringAreaDeg(ring: Array<[number, number]>): number {
  let s = 0;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    s += (ring[j]![0] + ring[i]![0]) * (ring[j]![1] - ring[i]![1]);
  }
  return Math.abs(s) / 2;
}

/** 点(x,y)がリング内部か (ray casting)。 */
function pointInRing(x: number, y: number, ring: Array<[number, number]>): boolean {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const xi = ring[i]![0], yi = ring[i]![1];
    const xj = ring[j]![0], yj = ring[j]![1];
    if (yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) {
      inside = !inside;
    }
  }
  return inside;
}

const FOOTPRINT_GROUNDS_AMENITY =
  /^(school|kindergarten|college|university|community_centre|community_hall|townhall|public_building|library|social_facility|hall)$/;
const FOOTPRINT_GROUNDS_LEISURE =
  /^(park|garden|recreation_ground|sports_centre|stadium|track|golf_course)$/;

/**
 * fetchFacilityFootprint の選択方針。
 *
 * - 'grounds-first' (既定): 避難先向け。施設の敷地全体を塗りたい。
 * - 'building-only': 物件自身向け。敷地ではなく建物の輪郭を塗りたい。
 *
 * この区別は必須。物件座標が公園や学校の敷地内に落ちると 'grounds-first' は
 * 「内包する最大の敷地」を返すため、例えば物件の塗りとして公園全体を掴む。
 */
export type FootprintMode = 'grounds-first' | 'building-only';

/**
 * 「正確な範囲」を名称に依存せず取得する。与えられた座標を **内包する** OSM
 * ポリゴンを採用する (名称表記ゆれの施設に有効)。
 * - mode='grounds-first' (避難先): 施設エリア(grounds: amenity=学校等 /
 *   leisure=park等 / landuse=recreation_ground)を優先し、内包する中で
 *   最大面積(=施設全体の敷地)を選ぶ。grounds が無ければ内包する建物の最小面積。
 * - mode='building-only' (物件): 内包する建物の最小面積(=その建物)だけを見る。
 *   敷地は候補にしない。建物が無ければ null。
 * - 点を内包するポリゴンが無ければ null (近接するだけの別施設を誤採用しない)。
 */
export async function fetchFacilityFootprint(
  center: { lng: number; lat: number },
  radiusMeters = 90,
  mode: FootprintMode = 'grounds-first',
): Promise<Array<[number, number]> | null> {
  const { lng, lat } = center;
  const amenityRe =
    'school|kindergarten|college|university|community_centre|community_hall|townhall|public_building|library|social_facility|hall';
  const query = `
    [out:json][timeout:25];
    (
      way["leisure"](around:${radiusMeters},${lat},${lng});
      way["amenity"~"${amenityRe}"](around:${radiusMeters},${lat},${lng});
      way["landuse"~"recreation_ground"](around:${radiusMeters},${lat},${lng});
      way["building"](around:${radiusMeters},${lat},${lng});
      relation["leisure"](around:${radiusMeters},${lat},${lng});
      relation["amenity"~"${amenityRe}"](around:${radiusMeters},${lat},${lng});
    );
    out tags geom;
  `;
  const data = await fetchOverpass(query);

  interface Cand {
    ring: Array<[number, number]>;
    area: number;
    grounds: boolean;
    building: boolean;
  }
  const cands: Cand[] = [];
  const addRing = (
    geom: Array<{ lon: number; lat: number }> | undefined,
    tags: Record<string, string>,
  ) => {
    if (!geom || geom.length < 4) return;
    const ring = geom.map((g) => [g.lon, g.lat] as [number, number]);
    const grounds =
      (!!tags['amenity'] && FOOTPRINT_GROUNDS_AMENITY.test(tags['amenity'])) ||
      (!!tags['leisure'] && FOOTPRINT_GROUNDS_LEISURE.test(tags['leisure'])) ||
      tags['landuse'] === 'recreation_ground';
    cands.push({ ring, area: ringAreaDeg(ring), grounds, building: !!tags['building'] });
  };
  for (const el of data.elements) {
    const tags = el.tags ?? {};
    if (el.geometry) addRing(el.geometry, tags);
    if (el.members) {
      for (const m of el.members) {
        if (m.role === 'outer' && m.geometry) addRing(m.geometry, tags);
      }
    }
  }

  const containing = cands.filter((c) => pointInRing(lng, lat, c.ring));
  if (containing.length === 0) return null;

  if (mode === 'building-only') {
    // 物件の塗り: 敷地は使わない。内包する建物のうち最小 (=その建物そのもの)。
    const buildings = containing.filter((c) => c.building);
    if (buildings.length === 0) return null;
    const smallest = buildings.reduce((a, b) => (b.area < a.area ? b : a));
    return closeRing(smallest.ring.slice());
  }

  const grounds = containing.filter((c) => c.grounds);
  let best: Cand;
  if (grounds.length > 0) {
    // 施設敷地全体を範囲に (内包する grounds のうち最大)。
    best = grounds.reduce((a, b) => (b.area > a.area ? b : a));
  } else {
    const buildings = containing.filter((c) => c.building);
    const pool = buildings.length > 0 ? buildings : containing;
    // 建物は最小面積(=その建物そのもの)。
    best = pool.reduce((a, b) => (b.area < a.area ? b : a));
  }

  return closeRing(best.ring.slice());
}

/** 始点と終点が一致していなければ閉じる (GeoJSON の閉リング要件)。 */
function closeRing(ring: Array<[number, number]>): Array<[number, number]> {
  const f = ring[0]!;
  const l = ring[ring.length - 1]!;
  if (f[0] !== l[0] || f[1] !== l[1]) ring.push([f[0], f[1]]);
  return ring;
}

/**
 * Circuit breaker: 全エンドポイントが失敗した直後の `COOLDOWN_MS` の間は
 * fetch そのものをスキップしてエラー投げる (即時 fail-fast)。
 *
 * - 429 (Too Many Requests) や ERR_CONNECTION_REFUSED が連発するときに
 *   毎フォローアップ呼び出しで6回のtry/catchを繰り返すと console が氾濫する。
 * - injectSampleProperty + resolveStationLabels が並列で ~10件の Overpass を投げると
 *   全部 fail → 各6 endpoint試行 = 60回のFAIL ログ。
 *
 * cooldown 中は OverpassError を即座に throw して呼び出し側の catch にfallthrough。
 */
let breakerUntilMs = 0;
const COOLDOWN_MS = 60_000;

/**
 * 応答キャッシュ (成功のみ・セッション限り)。
 *
 * Overpass は1往復が数秒かかる。同じ避難先の再作成・編集ページの再取得で
 * 同一クエリを何度も投げていたため、クエリ文字列をキーにメモ化する。
 * 失敗はキャッシュしない (一時障害を焼き付けないため)。
 */
const responseCache = new Map<string, OverpassResponse>();
/** 同一クエリの同時実行を1本にまとめる (2避難先の並列化で衝突しうる)。 */
const inflightQueries = new Map<string, Promise<OverpassResponse>>();
const CACHE_MAX = 50;

/**
 * 1エンドポイントあたりのタイムアウト。
 *
 * 3エンドポイントを直列に試すため、ユーザーの最悪待ち時間はこの3倍になる
 * (旧: 30秒 × 3 = 90秒)。Overpass のサーバ側上限は [timeout:25] だが、
 * この用途のクエリ (小範囲の建物/入口/橋) は通常数秒で返る。失敗しても
 * 呼び出し側に非致命フォールバック (簡易円 / 境界最近接点) があるため、
 * 「速く諦める」側に倒す。
 */
const ENDPOINT_TIMEOUT_MS = 12_000;

async function fetchOverpass(query: string): Promise<OverpassResponse> {
  const cached = responseCache.get(query);
  if (cached) return cached;
  const inflight = inflightQueries.get(query);
  if (inflight) return inflight;

  const run = fetchOverpassUncached(query);
  inflightQueries.set(query, run);
  try {
    const result = await run;
    if (responseCache.size >= CACHE_MAX) {
      const oldest = responseCache.keys().next().value;
      if (oldest !== undefined) responseCache.delete(oldest);
    }
    responseCache.set(query, result);
    return result;
  } finally {
    inflightQueries.delete(query);
  }
}

async function fetchOverpassUncached(
  query: string,
): Promise<OverpassResponse> {
  if (Date.now() < breakerUntilMs) {
    throw new OverpassError(
      'Overpass API cooldown中 (前回失敗から60秒待機)',
    );
  }
  let lastErr: unknown;
  for (const endpoint of OVERPASS_ENDPOINTS) {
    try {
      const ctl = new AbortController();
      const timer = setTimeout(() => ctl.abort(), ENDPOINT_TIMEOUT_MS);
      let response: Response;
      try {
        response = await fetch(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: new URLSearchParams({ data: query }).toString(),
          signal: ctl.signal,
        });
      } finally {
        clearTimeout(timer);
      }
      if (!response.ok) {
        throw new OverpassError(
          `Overpass APIがエラー (${response.status}): ${endpoint}`,
        );
      }
      // Content-Type が JSON でない場合 (HTML エラーページなど) は弾く
      const ct = response.headers.get('content-type') ?? '';
      if (!ct.includes('json')) {
        throw new OverpassError(
          `Overpass APIが非JSON応答 (${ct}): ${endpoint}`,
        );
      }
      const text = await response.text();
      let parsed: OverpassResponse;
      try {
        parsed = JSON.parse(text) as OverpassResponse;
      } catch (e) {
        throw new OverpassError(
          `Overpass APIの応答parse失敗: ${endpoint}`,
          e,
        );
      }
      // Overpass の RAW エラー (`remark` フィールド) を検知
      const remark = (parsed as { remark?: string }).remark;
      if (remark && /error|too busy|timeout|abort/i.test(remark)) {
        throw new OverpassError(
          `Overpass APIサーバ側エラー: ${remark} (${endpoint})`,
        );
      }
      console.info(`[overpass] success via ${endpoint}`);
      return parsed;
    } catch (e) {
      console.warn(`[overpass] failed ${endpoint}:`, e);
      lastErr = e;
      // 次のエンドポイントを試す
    }
  }
  // 全エンドポイント失敗 → cooldown を仕掛けて以降の呼び出しをfail-fast。
  breakerUntilMs = Date.now() + COOLDOWN_MS;
  throw new OverpassError(
    'Overpass APIへの接続に失敗しました (全エンドポイント不可)',
    lastErr,
  );
}

function escapeRegex(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * ランドマーク検索結果。
 * - 駅 (建物way) や 公園のようにOSMにpolygonがある場合は shape に閉じた多角形 (lng,lat) を返す
 * - subway nodeしかない場合などは shape=undefined、coordsのみ
 */
export interface LandmarkLookupResult {
  coords: { lng: number; lat: number };
  /** OSM way/relation の閉じた境界 (始点=終点)。あれば map にfill polygonとして描画可能 */
  shape?: Array<[number, number]>;
}

/**
 * 駅事業者 → OSM `operator` タグ値の正規表現パターン。
 *
 * OSMの operator タグは表記揺れあり (英名/和名/略称/法人格付き/旧名) なので "|" で複数許容。
 *
 * 東京メトロ系は特に揺れが多い:
 *   - 「東京メトロ」(ブランド名) / 「東京地下鉄」(法人名) は同一事業者
 *   - 「東京地下鉄株式会社」(完全法人名) / 「東京メトロ株式会社」も同一
 *   - 旧「営団」「帝都高速度交通営団 (Teito Rapid Transit Authority / Eidan)」も歴史的に同一会社
 *   - 英語表記: "Tokyo Metro" / "Tokyo Metro Co., Ltd."
 *
 * 各事業者のキャノニカル名 (日本語) はキー側のコメントに記載。
 */
export const STATION_OPERATOR_OSM_PATTERNS = {
  /** キャノニカル: 東日本旅客鉄道株式会社 (通称 JR東日本) */
  'jr-east':
    'JR\\s*East|East Japan Railway( Company)?|東日本旅客鉄道(株式会社)?|JR東日本',
  /** キャノニカル: 東京地下鉄株式会社 (ブランド名 東京メトロ)。旧 帝都高速度交通営団 = 営団 / Eidan */
  'tokyo-metro':
    'Tokyo\\s*Metro( Co\\.?,?\\s*Ltd\\.?)?|東京メトロ(株式会社)?|東京地下鉄(株式会社)?|帝都高速度交通営団|営団(地下鉄)?|Teito( Rapid Transit Authority)?|Eidan',
  /** キャノニカル: 東京都交通局 (通称 都営) */
  toei:
    'Toei( Subway)?|Tokyo Metropolitan Bureau of Transportation|Bureau of Transportation|東京都交通局|都営(地下鉄|交通局)?',
} as const;

export interface FetchLandmarkOptions {
  /**
   * OSMの `operator` タグで絞り込む正規表現パターン。
   * 同名駅の事業者違い (例: JR水道橋 vs 三田線水道橋) を区別したい時に使用。
   * `STATION_OPERATOR_OSM_PATTERNS` を参照して指定する。
   */
  operatorPattern?: string;
}

/**
 * OSMで「ある名前の地物」の座標と (可能なら) 形状を引く。
 * - 駅 (railway=station / subway) 優先で座標選定
 * - building=train_station / public_transport=station の way があれば shape も返す
 * - 大学・施設・公園・市役所も同様
 * - options.operatorPattern が指定されれば、OSM `operator` タグでさらに絞り込む
 *
 * 戻り値はマッチしない場合 null。
 */
export async function fetchLandmarkCoord(
  name: string,
  center: { lng: number; lat: number },
  radiusMeters = 2000,
  options?: FetchLandmarkOptions,
): Promise<LandmarkLookupResult | null> {
  const safeName = escapeRegex(name);
  // 改行を含むラベル (例「中央大学\n後楽園キャンパス」) は最初の語のみで検索
  const searchName = name.split(/[\n\s]/)[0] ?? name;
  const safeSearch = escapeRegex(searchName);

  // operator フィルタ (オプション): name一致した要素のうち、さらに OSM `operator` タグが
  // 指定パターンにマッチするものだけに絞る。同名駅の事業者違いを区別する用途。
  const opFilter = options?.operatorPattern
    ? `["operator"~"${options.operatorPattern}"]`
    : '';

  const query = `
    [out:json][timeout:25];
    (
      node["name"~"^${safeName}$|^${safeSearch}$"]${opFilter}(around:${radiusMeters},${center.lat},${center.lng});
      way["name"~"^${safeName}$|^${safeSearch}$"]${opFilter}(around:${radiusMeters},${center.lat},${center.lng});
      relation["name"~"^${safeName}$|^${safeSearch}$"]${opFilter}(around:${radiusMeters},${center.lat},${center.lng});
    );
    out tags center geom;
  `;
  let data: OverpassResponse;
  try {
    data = await fetchOverpass(query);
  } catch {
    return null;
  }
  // operator フィルタで0件の場合、operator無しで再検索 (OSMが operator タグ付けしてないケース対応)
  if (data.elements.length === 0 && opFilter) {
    const fallbackQuery = `
      [out:json][timeout:25];
      (
        node["name"~"^${safeName}$|^${safeSearch}$"](around:${radiusMeters},${center.lat},${center.lng});
        way["name"~"^${safeName}$|^${safeSearch}$"](around:${radiusMeters},${center.lat},${center.lng});
        relation["name"~"^${safeName}$|^${safeSearch}$"](around:${radiusMeters},${center.lat},${center.lng});
      );
      out tags center geom;
    `;
    try {
      data = await fetchOverpass(fallbackQuery);
    } catch {
      return null;
    }
  }
  if (data.elements.length === 0) return null;

  // 優先順位: 駅 > 駅建物 > station_area > 大学 > 市役所 > 公園 > その他
  const score = (el: OverpassElement): number => {
    const t = el.tags ?? {};
    if (t['railway'] === 'station' || t['subway']) return 100;
    if (t['building'] === 'train_station' || t['building'] === 'station') return 95;
    if (t['public_transport'] === 'station_area' || t['public_transport'] === 'station') return 90;
    if (t['amenity'] === 'university') return 60;
    if (t['amenity'] === 'townhall' || t['government']) return 55;
    if (t['leisure'] === 'park' || t['leisure'] === 'garden') return 50;
    if (t['name'] === name) return 40;
    if (t['building']) return 30;
    return 10;
  };
  const ranked = [...data.elements].sort((a, b) => score(b) - score(a));

  // 座標 (最高スコアの要素)
  let coords: { lng: number; lat: number } | null = null;
  for (const el of ranked) {
    if (el.type === 'node') {
      const n = el as unknown as { lat?: number; lon?: number };
      if (typeof n.lat === 'number' && typeof n.lon === 'number') {
        coords = { lng: n.lon, lat: n.lat };
        break;
      }
    }
    const c = (el as unknown as { center?: { lat: number; lon: number } }).center;
    if (c) {
      coords = { lng: c.lon, lat: c.lat };
      break;
    }
    if (el.geometry && el.geometry.length > 0) {
      const sum = el.geometry.reduce(
        (a, p) => ({ lng: a.lng + p.lon, lat: a.lat + p.lat }),
        { lng: 0, lat: 0 },
      );
      coords = {
        lng: sum.lng / el.geometry.length,
        lat: sum.lat / el.geometry.length,
      };
      break;
    }
  }
  if (!coords) return null;

  // shape: name一致した way (closed geometry) を優先スコア順で探す。
  // 駅shapeの取得方針は別途リサーチ中 (docs/research/station-shapes.md 参照)。
  // 現段階では「name一致のwayがある場合のみ」shape を返す。
  let shape: Array<[number, number]> | undefined;
  for (const el of ranked) {
    if (el.type !== 'way') continue;
    const ring = extractClosedRing(el.geometry);
    if (ring) {
      shape = ring;
      break;
    }
  }

  return shape ? { coords, shape } : { coords };
}

/**
 * OSM way geometry を閉じた多角形リング (lng,lat) に変換。
 * - 3頂点以下なら null (polygon にならない)
 * - 閉じてなければ始点を末尾に追加して閉じる
 */
function extractClosedRing(
  geometry: Array<{ lat: number; lon: number }> | undefined,
): Array<[number, number]> | null {
  if (!geometry || geometry.length < 3) return null;
  const ring: Array<[number, number]> = geometry.map((p) => [p.lon, p.lat]);
  const first = ring[0]!;
  const last = ring[ring.length - 1]!;
  if (first[0] !== last[0] || first[1] !== last[1]) {
    ring.push([first[0], first[1]]);
  }
  if (ring.length < 4) return null; // 3頂点+閉じる点 = 4
  return ring;
}

/**
 * OSMの入り口候補ノード。
 * - entrance=main/yes/secondary など、もしくは barrier=gate (徒歩可) も該当
 */
export interface EntranceNode {
  id: number;
  lng: number;
  lat: number;
  /** OSMのentranceタグ値 (main/yes/secondary/serviceなど)。barrier=gateの場合は'gate' */
  kind: string;
  /** 名前があれば */
  name?: string;
}

/**
 * 指定ポリゴン内のOSM入り口ノードを取得する。
 * - `entrance=*` (main/yes/secondary など) → 公園/施設の入口
 * - `barrier=gate` で `foot=yes` or `access=yes` → 通行可能な門
 *
 * Overpassの `(poly:"lat1 lng1 lat2 lng2 ...")` フィルタを使用。
 *
 * `bufferMeters` だけポリゴンを外側に膨らませてから検索する。校門・公園の門は
 * 塀の線上=境界に載ることが多く、簡略化したポリゴンだと僅かに外側へはみ出して
 * 厳密内包では取りこぼすため、既定で ~25m の余白を持たせる。
 */
export async function fetchEntrancesInPolygon(
  polygonRing: Array<[number, number]>,
  bufferMeters = 25,
): Promise<EntranceNode[]> {
  if (polygonRing.length < 3) return [];

  // 検索用リング: 既定でポリゴンを外側に buffer (境界・直外の門も拾う)
  let searchRing = polygonRing;
  if (bufferMeters > 0) {
    try {
      const closed = [...polygonRing];
      const f = closed[0]!;
      const l = closed[closed.length - 1]!;
      if (f[0] !== l[0] || f[1] !== l[1]) closed.push([f[0], f[1]]);
      const buffered = turfBuffer(turfPolygon([closed]), bufferMeters, {
        units: 'meters',
      });
      const bRing =
        buffered && buffered.geometry.type === 'Polygon'
          ? buffered.geometry.coordinates[0]
          : null;
      if (bRing && bRing.length >= 4) {
        searchRing = bRing.map(([lng, lat]) => [lng, lat] as [number, number]);
      }
    } catch {
      // buffer失敗時は元のリングで検索
    }
  }

  // (poly:"lat1 lng1 lat2 lng2 ...") 形式: "lat lng" のスペース区切り
  const polyParam = searchRing
    .map(([lng, lat]) => `${lat} ${lng}`)
    .join(' ');

  const query = `
    [out:json][timeout:25];
    (
      node["entrance"](poly:"${polyParam}");
      node["barrier"="gate"](poly:"${polyParam}");
    );
    out tags geom;
  `;

  const data = await fetchOverpass(query);
  const out: EntranceNode[] = [];
  for (const el of data.elements) {
    if (el.type !== 'node') continue;
    // OverpassのNodeは geometry が単点になっている (out tags geom)
    const geom = (el as unknown as { lat?: number; lon?: number });
    const lat = geom.lat;
    const lng = geom.lon;
    if (typeof lat !== 'number' || typeof lng !== 'number') continue;
    const tags = el.tags ?? {};
    // 徒歩で通れない gate を除外 (foot=no / access=private 等)
    if (tags['barrier'] === 'gate') {
      const foot = tags['foot'];
      const access = tags['access'];
      if (foot === 'no' || access === 'no' || access === 'private') continue;
    }
    out.push({
      id: el.id,
      lng,
      lat,
      kind: tags['entrance'] ?? tags['barrier'] ?? 'unknown',
      name: tags['name'],
    });
  }
  return out;
}

export interface BridgeCrossing {
  lng: number;
  lat: number;
  name?: string;
  highway: string;
  /** start→end 直線からの垂直距離 (m)。小さいほど「直線上の橋」。 */
  perpMeters: number;
}

/** 徒歩で渡れる橋か (highway種別 + foot/access で判定)。 */
function isWalkableBridge(t: Record<string, string>): boolean {
  const hw = t['highway'];
  if (!hw) return false;
  // 自動車専用は歩行不可
  if (
    hw === 'motorway' ||
    hw === 'motorway_link' ||
    hw === 'trunk' ||
    hw === 'trunk_link' ||
    hw === 'construction' ||
    hw === 'proposed' ||
    hw === 'raceway'
  ) {
    return false;
  }
  if (t['foot'] === 'no') return false;
  // access制限があっても foot=yes/designated なら通す
  if (t['access'] === 'no' || t['access'] === 'private') {
    if (t['foot'] !== 'yes' && t['foot'] !== 'designated') return false;
  }
  return true;
}

/**
 * start→end の直線近傍にある「徒歩で渡れる橋」の横断点を返す。
 *
 * 用途: 川等を渡る経路で Mapbox の徒歩ルート探索が最短でない橋を選ぶことがあるため
 * (実測: 無指定より橋経由の方が短いケースあり)、近隣の橋を明示的に経由地にして
 * 「どの橋で渡るか」の候補を作る。橋ジオメトリ上で**直線に最も近い点**を横断点として
 * 返す (= 直線が渡りたい位置)。直線から perpMeters が近い順、最大 maxBridges 本。
 * Overpass 障害時は OverpassError を投げる (呼び出し側で握りつぶす)。
 */
export async function fetchWalkableBridgesNearSegment(
  a: { lng: number; lat: number },
  b: { lng: number; lat: number },
  corridorMeters = 220,
  maxBridges = 4,
): Promise<BridgeCrossing[]> {
  const latPad = corridorMeters / 111_000;
  const midLat = (a.lat + b.lat) / 2;
  const lngPad =
    corridorMeters / (111_000 * Math.max(Math.cos((midLat * Math.PI) / 180), 0.2));
  const s = Math.min(a.lat, b.lat) - latPad;
  const n = Math.max(a.lat, b.lat) + latPad;
  const w = Math.min(a.lng, b.lng) - lngPad;
  const e = Math.max(a.lng, b.lng) + lngPad;

  const query = `
    [out:json][timeout:25];
    ( way["bridge"]["highway"](${s},${w},${n},${e}); );
    out tags geom;
  `;
  const data = await fetchOverpass(query);

  const seg = turfLineString([
    [a.lng, a.lat],
    [b.lng, b.lat],
  ]);
  const segLenKm = turfLength(seg, { units: 'kilometers' });
  const out: BridgeCrossing[] = [];
  for (const el of data.elements) {
    if (el.type !== 'way' || !el.geometry || el.geometry.length < 2) continue;
    const t = el.tags ?? {};
    if (!isWalkableBridge(t)) continue;
    // 橋ジオメトリの頂点のうち、直線に最も近い点を横断点にする
    let best: { lng: number; lat: number; perp: number; loc: number } | null =
      null;
    for (const g of el.geometry) {
      const np = nearestPointOnLine(seg, turfPoint([g.lon, g.lat]), {
        units: 'kilometers',
      });
      const perp = (np.properties.dist ?? Infinity) * 1000;
      const loc = np.properties.location ?? 0;
      if (!best || perp < best.perp) {
        best = { lng: g.lon, lat: g.lat, perp, loc };
      }
    }
    if (!best) continue;
    const frac = segLenKm > 0 ? best.loc / segLenKm : 0.5;
    // 直線の外側(端点より手前/先)の橋は横断に無関係なので除外
    if (best.perp <= corridorMeters && frac > -0.05 && frac < 1.05) {
      out.push({
        lng: best.lng,
        lat: best.lat,
        name: t['name'],
        highway: t['highway']!,
        perpMeters: Math.round(best.perp),
      });
    }
  }
  out.sort((x, y) => x.perpMeters - y.perpMeters);
  return out.slice(0, maxBridges);
}
